import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Viewer } from '@/viewer/Viewer';
import {
  buildMeshIndex,
  collectMeshes,
  detachByIDs,
  setVisibilityByIDs,
  type MeshIndex,
} from '@/viewer/modelOps';
import { DeleteHistory } from '@/viewer/deleteHistory';
import { buildSectionPlane, sectionRange } from '@/viewer/section';
import { directionForView, type StandardView } from '@/viewer/standardViews';
import { IFCLoader } from '@/ifc/IFCLoader';
import { findNodeLabel } from '@/ifc/modelTree';
import { useViewerStore, type SectionState } from '@/state/viewerStore';

interface ViewerContextValue {
  attachCanvas: (canvas: HTMLCanvasElement) => void;
  loadFile: (file: File) => Promise<void>;
  loadSample: () => Promise<void>;
  deselect: () => void;
  fitView: () => void;
  topView: () => void;
  resetView: () => void;
  hideSelected: () => void;
  isolateSelected: () => void;
  showAll: () => void;
  screenshot: () => Promise<void>;
  selectElement: (expressID: number, zoom?: boolean) => void;
  setElementsVisible: (expressIDs: number[], visible: boolean) => void;
  deleteSelected: () => void;
  undoDelete: () => void;
  restoreAllDeleted: () => void;
  zoomToSelection: () => void;
  toggleEdges: () => void;
  toggleMeasureMode: () => void;
  clearMeasurements: () => void;
  updateSection: (changes: Partial<SectionState>) => void;
  toggleSection: () => void;
  toggleProjection: () => void;
  setStandardView: (view: StandardView) => void;
  getViewer: () => Viewer | null;
}

const ViewerContext = createContext<ViewerContextValue | null>(null);

export function ViewerProvider({ children }: { children: ReactNode }) {
  const viewerRef = useRef<Viewer | null>(null);
  const loaderRef = useRef<IFCLoader>(new IFCLoader());
  const [, force] = useState(0);

  const setReady = useViewerStore((s) => s.setReady);
  const setLoad = useViewerStore((s) => s.setLoad);
  const resetLoad = useViewerStore((s) => s.resetLoad);
  const setModel = useViewerStore((s) => s.setModel);
  const pushToast = useViewerStore((s) => s.pushToast);
  const setSelection = useViewerStore((s) => s.setSelection);
  const setProperties = useViewerStore((s) => s.setProperties);
  const setPropertiesLoading = useViewerStore((s) => s.setPropertiesLoading);
  const setStats = useViewerStore((s) => s.setStats);
  const setTree = useViewerStore((s) => s.setTree);
  const setHiddenIDs = useViewerStore((s) => s.setHiddenIDs);
  const setDeletedIDs = useViewerStore((s) => s.setDeletedIDs);
  const setEdgesEnabled = useViewerStore((s) => s.setEdgesEnabled);
  const setLengthUnit = useViewerStore((s) => s.setLengthUnit);
  const setMeasureMode = useViewerStore((s) => s.setMeasureMode);
  const setMeasurements = useViewerStore((s) => s.setMeasurements);
  const setSection = useViewerStore((s) => s.setSection);
  const setProjection = useViewerStore((s) => s.setProjection);
  const propsReqRef = useRef(0);
  const loadSeqRef = useRef(0);
  const meshIndexRef = useRef<MeshIndex>(new Map());
  const deleteHistoryRef = useRef(new DeleteHistory());

  useEffect(() => {
    setLoad({ message: 'Initializing IFC engine…', percent: 5 });
    loaderRef.current
      .init()
      .then(() => {
        setReady(true);
        resetLoad();
      })
      .catch((err: Error) => {
        resetLoad();
        pushToast({ kind: 'error', message: `Init failed: ${err.message}` });
      });

    return () => {
      // Null the ref so a remount (e.g. StrictMode's simulated unmount in
      // dev) lets attachCanvas create a fresh viewer instead of keeping a
      // disposed one forever.
      viewerRef.current?.dispose();
      viewerRef.current = null;
      loaderRef.current.dispose();
    };
  }, [setReady, setLoad, resetLoad, pushToast]);

  const value = useMemo<ViewerContextValue>(
    () => ({
      attachCanvas: (canvas) => {
        if (viewerRef.current) return;
        const viewer = new Viewer(canvas);
        viewer.onStats((s) => setStats(s));
        viewer.selection.onChange((info) => {
          setSelection({
            expressID: info?.expressID ?? null,
            meshName: info?.meshName ?? null,
          });
          const id = info?.expressID ?? null;
          // orbit around what was just picked, not the model centre
          if (id !== null) viewer.pivotOnObjects(meshIndexRef.current.get(id) ?? []);
          const reqId = ++propsReqRef.current;
          if (id === null) {
            setProperties([]);
            setPropertiesLoading(false);
            return;
          }
          setPropertiesLoading(true);
          loaderRef.current
            .getProperties(id)
            .then((groups) => {
              if (reqId !== propsReqRef.current) return;
              setProperties(groups);
              setPropertiesLoading(false);
            })
            .catch((err: Error) => {
              if (reqId !== propsReqRef.current) return;
              setProperties([]);
              setPropertiesLoading(false);
              pushToast({ kind: 'error', message: `Failed to load properties: ${err.message}` });
            });
        });
        viewer.measurements.onChange((measurements) => setMeasurements(measurements));
        viewerRef.current = viewer;
        if (import.meta.env.DEV) {
          (window as unknown as Record<string, unknown>)['__viewer'] = viewer;
        }
        force((n) => n + 1);
      },
      deselect: () => {
        viewerRef.current?.selection.deselect();
      },
      fitView: () => {
        viewerRef.current?.fitToObject();
      },
      topView: () => {
        viewerRef.current?.topView();
      },
      resetView: () => {
        viewerRef.current?.resetView();
        setHiddenIDs(new Set());
      },
      hideSelected: () => {
        const id = useViewerStore.getState().selection.expressID;
        if (id === null) return;
        applyVisibility([id], false);
        viewerRef.current?.selection.deselect();
      },
      isolateSelected: () => {
        const id = useViewerStore.getState().selection.expressID;
        if (id === null) return;
        const others = [...meshIndexRef.current.keys()].filter((k) => k !== id);
        setVisibilityByIDs(meshIndexRef.current, others, false);
        setVisibilityByIDs(meshIndexRef.current, [id], true);
        viewerRef.current?.invalidate();
        setHiddenIDs(new Set(others));
      },
      showAll: () => {
        viewerRef.current?.showAll();
        setHiddenIDs(new Set());
      },
      selectElement: (expressID, zoom = false) => {
        const viewer = viewerRef.current;
        const meshes = meshIndexRef.current.get(expressID);
        if (!viewer || !meshes || meshes.length === 0) return;
        viewer.selection.select(meshes[0]!);
        if (zoom) viewer.focusOn(meshes);
      },
      setElementsVisible: (expressIDs, visible) => {
        applyVisibility(expressIDs, visible);
      },
      deleteSelected: () => {
        const id = useViewerStore.getState().selection.expressID;
        if (id === null) return;
        deleteElements([id]);
      },
      undoDelete: () => {
        const viewer = viewerRef.current;
        const restored = deleteHistoryRef.current.undo();
        if (!viewer || restored.length === 0) return;
        syncDeletedIDs();
        viewer.invalidate();
        // put the user back where they were: the restored element reselected
        const meshes = meshIndexRef.current.get(restored[0]!);
        if (meshes && meshes.length > 0) viewer.selection.select(meshes[0]!);
      },
      restoreAllDeleted: () => {
        const viewer = viewerRef.current;
        const restored = deleteHistoryRef.current.restoreAll();
        if (!viewer || restored.length === 0) return;
        syncDeletedIDs();
        viewer.invalidate();
      },
      zoomToSelection: () => {
        const viewer = viewerRef.current;
        if (!viewer) return;
        const id = useViewerStore.getState().selection.expressID;
        const meshes = id !== null ? meshIndexRef.current.get(id) : undefined;
        if (meshes && meshes.length > 0) viewer.focusOn(meshes);
        else viewer.fitToObject();
      },
      toggleEdges: () => {
        const viewer = viewerRef.current;
        if (!viewer) return;
        const next = !useViewerStore.getState().edgesEnabled;
        viewer.setEdges(next);
        setEdgesEnabled(next);
      },
      toggleMeasureMode: () => {
        const viewer = viewerRef.current;
        if (!viewer) return;
        const next = !useViewerStore.getState().measureMode;
        viewer.setMeasureMode(next);
        setMeasureMode(next);
      },
      clearMeasurements: () => {
        viewerRef.current?.measurements.clear();
      },
      updateSection: (changes) => {
        applySection(changes);
      },
      toggleSection: () => {
        const { sectionOpen, setSectionOpen } = useViewerStore.getState();
        setSectionOpen(!sectionOpen);
        applySection({ enabled: !sectionOpen });
      },
      toggleProjection: () => {
        const viewer = viewerRef.current;
        if (!viewer) return;
        const next =
          useViewerStore.getState().projection === 'orthographic'
            ? 'perspective'
            : 'orthographic';
        viewer.setProjection(next);
        setProjection(next);
      },
      setStandardView: (view) => {
        viewerRef.current?.fitToObject(directionForView(view));
      },
      getViewer: () => viewerRef.current,
      screenshot: async () => {
        const name = `ifc-navigator-${new Date().toISOString().replace(/[:.]/g, '-')}.png`;
        await viewerRef.current?.screenshot(name);
      },
      loadFile: async (file) => {
        await loadBuffer(file.name, await file.arrayBuffer());
      },
      loadSample: async () => {
        const name = 'sample.ifc';
        setLoad({ message: 'Fetching sample model…', percent: 5 });
        try {
          const response = await fetch('/sample.ifc', { cache: 'force-cache' });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const buffer = await response.arrayBuffer();
          await loadBuffer(name, buffer);
        } catch (err) {
          resetLoad();
          const msg = (err as Error).message ?? 'Unknown error';
          pushToast({ kind: 'error', message: `Failed to load sample: ${msg}` });
          console.error(err);
        }
      },
    }),
    [
      setLoad,
      resetLoad,
      setModel,
      pushToast,
      setSelection,
      setProperties,
      setPropertiesLoading,
      setStats,
      setHiddenIDs,
      setDeletedIDs,
      setEdgesEnabled,
      setMeasureMode,
      setMeasurements,
      setSection,
      setProjection,
    ],
  );

  function applySection(changes: Partial<SectionState>): void {
    const viewer = viewerRef.current;
    if (!viewer) return;
    const next = { ...useViewerStore.getState().section, ...changes };
    if (changes.axis !== undefined && changes.offset === undefined) {
      // switching axis: re-derive the range and start at the far end
      const range = sectionRange(viewer.modelBox(), next.axis);
      next.min = range.min;
      next.max = range.max;
      next.offset = next.flip ? range.min : range.max;
    }
    setSection(next);
    viewer.setSectionPlane(
      next.enabled ? buildSectionPlane(next.axis, next.flip, next.offset) : null,
    );
  }

  function syncDeletedIDs(): void {
    setDeletedIDs(new Set(deleteHistoryRef.current.deletedIDs));
  }

  function deleteElements(expressIDs: number[]): void {
    const viewer = viewerRef.current;
    if (!viewer) return;
    const detached = detachByIDs(meshIndexRef.current, expressIDs);
    if (detached.length === 0) return;
    // drop the selection so Selection restores the original material and the
    // properties panel stops describing an element that is no longer there
    viewer.selection.deselect();
    deleteHistoryRef.current.push(expressIDs, detached);
    syncDeletedIDs();
    viewer.invalidate();
    const id = expressIDs[0]!;
    const label = findNodeLabel(useViewerStore.getState().tree, id) ?? `element #${id}`;
    pushToast({ kind: 'info', message: `Deleted ${label} — Ctrl+Z to undo` });
  }

  function applyVisibility(expressIDs: number[], visible: boolean): void {
    setVisibilityByIDs(meshIndexRef.current, expressIDs, visible);
    viewerRef.current?.invalidate();
    const next = new Set(useViewerStore.getState().hiddenIDs);
    for (const id of expressIDs) {
      if (visible) next.delete(id);
      else next.add(id);
    }
    setHiddenIDs(next);
  }

  async function loadBuffer(name: string, buffer: ArrayBuffer): Promise<void> {
    const viewer = viewerRef.current;
    if (!viewer) return;
    setLoad({ message: `Reading ${name}…`, percent: 10 });
    try {
      // reattach deleted meshes first: disposeModelTree only walks the scene
      // graph, so anything still detached would escape disposal
      deleteHistoryRef.current.restoreAll();
      viewer.clearModel();
      meshIndexRef.current = new Map();
      const { group, warningCount } = await loaderRef.current.load(buffer, (p) => {
        setLoad({ message: p.message, percent: p.percent ?? 50 });
      });
      viewer.modelRoot.add(group);
      viewer.fitToObject();
      const meshes = collectMeshes(group);
      meshIndexRef.current = buildMeshIndex(meshes);
      viewer.setEdges(useViewerStore.getState().edgesEnabled);
      viewer.measurements.clear();
      viewer.setMeasureMode(false);
      viewer.setSectionPlane(null);
      setLengthUnit(loaderRef.current.unit);
      setModel(name, meshes.length);
      const range = sectionRange(viewer.modelBox(), 'z');
      setSection({
        enabled: false,
        axis: 'z',
        flip: false,
        offset: range.max,
        min: range.min,
        max: range.max,
      });
      const seq = ++loadSeqRef.current;
      void loaderRef.current
        .getModelTree()
        .then((tree) => {
          if (seq === loadSeqRef.current) setTree(tree);
        })
        .catch((err: unknown) => console.warn('Failed to build model tree:', err));
      resetLoad();
      pushToast({
        kind: 'info',
        message:
          warningCount > 0
            ? `Loaded ${name} (${warningCount} parser ${warningCount === 1 ? 'warning' : 'warnings'})`
            : `Loaded ${name}`,
      });
    } catch (err) {
      resetLoad();
      const msg = (err as Error).message ?? 'Unknown error';
      pushToast({ kind: 'error', message: `Failed to load: ${msg}` });
      console.error(err);
    }
  }

  return <ViewerContext.Provider value={value}>{children}</ViewerContext.Provider>;
}

export function useViewer(): ViewerContextValue {
  const ctx = useContext(ViewerContext);
  if (!ctx) throw new Error('useViewer must be used inside <ViewerProvider>');
  return ctx;
}
